import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import WarningIcon from '@mui/icons-material/Warning';
import {
  Box,
  IconButton,
  InputBase,
  ListItem,
  ListItemSecondaryAction,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type React from 'react';
import { useEffect, useState } from 'react';
import type { ProposalHeading } from '../../types/proposal';
import AddSectionTrigger from './AddSectionTrigger';
import { extractTitleTags, restoreTitleTags } from './ReviewSections/utils';

interface SortableItemProps {
  id: string;
  item: ProposalHeading;
  index: number;
  onRemove: (index: number) => void;
  onUpdate: (id: string, newTitle: string) => void;
  onFocuserUpdate: (id: string, newFocuser: string) => void;
  onInsert: (index: number) => void;
  isDuplicate?: boolean;
}

const SortableItem: React.FC<SortableItemProps> = (props) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: props.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const theme = useTheme();
  const level = props.item.level || 1;
  const [cleanTitle, setCleanTitle] = useState(() => extractTitleTags(props.item.title).cleanTitle);
  const [tags, setTags] = useState(() => extractTitleTags(props.item.title).tags);
  const [focuser, setFocuser] = useState(props.item.focuser || '');

  useEffect(() => {
    const { cleanTitle: newClean, tags: newTags } = extractTitleTags(props.item.title);
    setCleanTitle(newClean);
    setTags(newTags);
  }, [props.item.title]);

  useEffect(() => {
    setFocuser(props.item.focuser || '');
  }, [props.item.focuser]);

  const handleBlur = () => {
    const fullTitle = restoreTitleTags(cleanTitle, tags);
    if (fullTitle !== props.item.title) {
      props.onUpdate(props.id, fullTitle);
    }
  };

  const handleFocuserBlur = () => {
    if (focuser !== (props.item.focuser || '')) {
      props.onFocuserUpdate(props.id, focuser);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      (e.target as HTMLInputElement).blur();
    }
  };

  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      {...attributes}
      dense
      sx={{
        borderBottom: `1px solid ${theme.palette.divider}`,
        '&:hover': { bgcolor: theme.palette.action.hover },
        pl: level === 2 ? 6 : level === 3 ? 10 : level === 4 ? 14 : 2,
        py: 0.5,
        position: 'relative', // Needed for AddSectionTrigger positioning
      }}
    >
      <Box
        sx={{ width: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1 }}
      >
        <Typography
          variant="caption"
          sx={{
            fontSize: '0.7rem',
            fontWeight: 700,
            color: theme.palette.greyContrast,
            pl: 0,
            pr: 0,
          }}
        >
          {`H${level}`}
        </Typography>
      </Box>
      <IconButton {...listeners} edge="start" aria-label="drag" size="small" sx={{ mr: 1 }}>
        <DragIndicatorIcon fontSize="small" />
      </IconButton>

      <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
        <InputBase
          value={cleanTitle}
          onChange={(e) => setCleanTitle(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          fullWidth
          multiline
          startAdornment={
            props.isDuplicate ? (
              <Tooltip title="Potential duplicate heading detected">
                <WarningIcon
                  data-testid="WarningIcon"
                  sx={{
                    color: theme.palette.bidlogiq.brick,
                    fontSize: '1rem',
                    mr: 1,
                    flexShrink: 0,
                  }}
                />
              </Tooltip>
            ) : null
          }
          sx={{
            fontSize: level === 2 ? '0.85rem' : level >= 3 ? '0.75rem' : '0.95rem',
            fontWeight: level === 2 ? 500 : level >= 3 ? 400 : 600,
            color: props.isDuplicate ? theme.palette.bidlogiq.brick : theme.palette.text.primary,
          }}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', ml: 2, mr: 1 }}>
          <InputBase
            placeholder="Add focus instruction (e.g. '400 words, refer to ISO9001')"
            value={focuser}
            onChange={(e) => {
              setFocuser(e.target.value.slice(0, 1000));
            }}
            onBlur={handleFocuserBlur}
            onKeyDown={handleKeyDown}
            multiline
            sx={{
              fontSize: '0.75rem',
              width: { xs: 150, sm: 250, md: 350 },
              borderBottom: `1px solid ${theme.palette.divider}`,
              color: theme.palette.text.secondary,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          />
        </Box>
      </Box>

      <ListItemSecondaryAction>
        <IconButton edge="end" aria-label="delete" onClick={() => props.onRemove(props.index)}>
          <DeleteIcon />
        </IconButton>
      </ListItemSecondaryAction>

      <AddSectionTrigger onAdd={() => props.onInsert(props.index + 1)} />
    </ListItem>
  );
};

export default SortableItem;
